import { motion } from "framer-motion";
import { Flame, Mountain, Truck, Heart } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

function ActNumber({ n }) {
  return (
    <span className="font-display text-6xl sm:text-7xl font-semibold text-amber-600/15 leading-none select-none" aria-hidden="true">
      {n}
    </span>
  );
}

function StoryImage({ src, alt, testid, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: EASE }}
      className={`overflow-hidden rounded-2xl border border-amber-900/10 shadow-xl shadow-amber-900/10 ${className}`}
    >
      <img src={src} alt={alt} data-testid={testid} loading="lazy"
        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 ease-out" />
    </motion.div>
  );
}

export default function OurStory() {
  return (
    <section id="our-story" data-testid="our-story-section" className="py-20 sm:py-28 bg-[#F5F0E6] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="eyebrow mb-4">02 · Our Story</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="our-story-heading">
            From Kainchi's Hills to the Texas Plains
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed">
            Every temple begins with a calling. Ours is a journey told in four moments.
          </p>
        </div>

        {/* Act I — The Pilgrimage */}
        <div className="mt-16 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          >
            <div className="flex items-start gap-5">
              <ActNumber n="I" />
              <div>
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold">
                  <Mountain className="h-4 w-4" /> The Pilgrimage
                </p>
                <h3 className="font-display text-xl sm:text-2xl text-amber-950 mt-2">A Prayer at Kainchi Dham</h3>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  In the spring of 2023, our story began in the Kumaon Himalayas — at Kainchi Dham near
                  Nainital, the ashram Neem Karoli Baba made his own, in the valley he said was beloved
                  of Hanuman-ji himself.
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  On April 29, 2023, our founder sat quietly for half an hour near Baba's bed and blanket at
                  Kainchi Dham, wearing his maroon Osho clothes and mala. Leaving Baba's room, a wish arose:
                  <span className="font-quote italic text-amber-900"> "Take Baba's photograph home to Texas — and someday, build Him a temple there."</span>
                </p>
              </div>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            <StoryImage src="/images/kainchi-friends.webp" alt="Our founder with friends at the gates of Kainchi Dham" testid="story-image-pilgrimage-1" className="row-span-2 aspect-[3/4]" />
            <StoryImage src="/images/kainchi-hug.webp" alt="Our founder and his daughter at the gates of Kainchi Dham" testid="story-image-pilgrimage-2" delay={0.12} className="aspect-[3/4]" />
            <StoryImage src="/images/kainchi-valley.webp" alt="Above Kainchi Dham — the valley where the wish was born" testid="story-image-pilgrimage-3" delay={0.2} className="aspect-[4/3]" />
          </div>
        </div>

        {/* Act II — Hanuman-ji Comes Home */}
        <div className="mt-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="grid grid-cols-3 gap-3 order-2 lg:order-1">
            <StoryImage src="/images/murti-arrival-1.jpg" alt="The Hanuman murti being lifted by crane in Denison" testid="story-image-arrival-1" className="aspect-[9/16]" />
            <StoryImage src="/images/murti-arrival-2.jpg" alt="Hanuman-ji arriving on a flatbed truck" testid="story-image-arrival-2" delay={0.12} className="aspect-[9/16] mt-6" />
            <StoryImage src="/images/murti-arrival-3.jpg" alt="The murti lowered gently onto temple ground" testid="story-image-arrival-3" delay={0.22} className="aspect-[9/16]" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-start gap-5">
              <ActNumber n="II" />
              <div>
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold">
                  <Truck className="h-4 w-4" /> The Arrival
                </p>
                <h3 className="font-display text-xl sm:text-2xl text-amber-950 mt-2">Hanuman-ji Comes Home to Denison</h3>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  And Baba answered — with a miracle hiding in plain sight. Waiting quietly in Odessa, Texas all
                  along was a fourteen-foot, six-ton black granite murti of Lord Hanuman, long mistaken for a
                  Buddha. On November 3, 2023, cranes lifted Him onto a flatbed and set Him gently upon Texas
                  earth in Denison, beneath a wide blue sky.
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  The day Hanuman-ji touched this ground, the temple was no longer a plan — it was a
                  presence. Today the doors at 1642 FM 1310 open every Saturday, and the mandir is
                  lovingly tended by a devoted family — a circle of devoted families around them,
                  and a board of trustees now forming.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Act III — The First Chalisa */}
        <div className="mt-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          >
            <div className="flex items-start gap-5">
              <ActNumber n="III" />
              <div>
                <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold">
                  <Flame className="h-4 w-4" /> The First Chalisa
                </p>
                <h3 className="font-display text-xl sm:text-2xl text-amber-950 mt-2">The First Prayer Beneath an Open Sky</h3>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  No roof, no walls — only grass, sky, and Hanuman-ji. The day after the murti was
                  installed — November 4, 2023 — the very first Hanuman Chalisa rose over this field,
                  and the first head bowed at His feet.
                </p>
                <p className="mt-4 text-base leading-relaxed text-stone-700">
                  On December 4, 2023, Baba's own mandir was inaugurated — a hand-carved wooden altar,
                  fresh marigolds, and Maharaj-ji's smiling face watching over it all. That is how this
                  temple truly began: not with bricks, but with a chant.
                </p>
              </div>
            </div>
          </motion.div>
          <div className="grid grid-cols-2 gap-4">
            <StoryImage src="/images/first-chalisa-bow.jpg" alt="The first bow at Hanuman-ji's feet after installation" testid="story-image-chalisa-1" className="row-span-2 aspect-[3/4]" />
            <StoryImage src="/images/first-chalisa-joy.jpg" alt="Pure joy at the first Hanuman Chalisa" testid="story-image-chalisa-2" delay={0.12} className="aspect-[3/4]" />
            <StoryImage src="/images/mandir-inauguration.jpg" alt="Baba's hand-carved altar at the mandir inauguration" testid="story-image-chalisa-3" delay={0.2} className="aspect-[4/3]" />
          </div>
        </div>

        {/* Act IV — The Dream */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.9, ease: EASE }}
          className="mt-24 rounded-3xl bg-[#1C1917] px-8 py-14 sm:px-16 text-center relative overflow-hidden"
          data-testid="our-story-dream"
        >
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(circle at 50% 0%, #F59E0B 0%, transparent 55%)" }} />
          <p className="relative flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold mb-8">
            <Heart className="h-4 w-4" /> IV · The Dream Ahead
          </p>
          <blockquote className="relative font-quote italic text-2xl sm:text-3xl lg:text-4xl text-amber-50 leading-snug max-w-4xl mx-auto" data-testid="our-story-vision-quote">
            "A place to come sit in silence and feel the presence of Baba, Hanuman and Ram —
            a place to drown in devotion and love for all."
          </blockquote>
          <p className="relative mt-6 text-sm text-stone-400 max-w-xl mx-auto leading-relaxed">
            This is our founder's dream for the new, bigger temple of Denison — and every brick of it
            will be built from love.
          </p>
          <a href="#donate" data-testid="our-story-donate-cta"
            className="relative mt-9 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-600 px-8 py-3.5 text-sm font-semibold text-white shadow-xl shadow-orange-950/40 hover:-translate-y-0.5 hover:shadow-orange-900/60 transition-all duration-300">
            <Flame className="flame h-4 w-4" /> Join This Dream
          </a>
        </motion.div>

        <div className="mt-10 text-center">
          <a href="/about-us" data-testid="our-story-about-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-amber-800 hover:text-orange-700 transition-colors">
            Meet the family behind the mandir →
          </a>
        </div>
      </div>
    </section>
  );
}
